import { useMutation, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { API_PATIENT_NOTE_DELETE } from 'services/dataService/ccmResources';
import { showResult } from 'infrastructure/hooks/utils';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store';
import { DateFormatter, isRpmModule } from 'infrastructure/functions';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { apiPatientClinicalNotesDelete } from 'services/dataService/resources';
import { DateFormats, UserType } from 'infrastructure/enums';
import {
  getCcmInteractionTimeKey,
  getRpmInteractionTimeKey,
} from 'components/organisms/interactiveTime/hooks';

import {
  CLINICAL_NITES_PER_PAGE,
  getClinicalNotesKey,
} from './use-get-clinical-notes';

import type {
  IClinicNotesResponse,
  IDeleteClinicNotes,
} from 'infrastructure/interfaces';
import type { ModuleType } from 'infrastructure/enums';

interface Props {
  page: number;
  patientGuid: string;
  module: ModuleType;
}

export const deleteClinicalNotesFn = async (postData: IDeleteClinicNotes) => {
  const { guid, module, patientGuid, userType } = postData;
  if (userType !== UserType.Admin) {
    throw new Error('You do not have permission to delete comments');
  }
  let url = API_PATIENT_NOTE_DELETE;

  if (isRpmModule(module)) url = apiPatientClinicalNotesDelete;

  const { data, error } = await dataService.createOne<void>(url, {
    guid,
    patientGuid,
  });

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

export const useDeleteClinicalNotes = ({
  page,
  module,
  patientGuid,
}: Props) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const [startTime, endTime] = useAppSelector<[Date, Date] | []>(
    (state) => state.ccmPatientDetails.dates,
  );

  const filter = {
    page,
    items: CLINICAL_NITES_PER_PAGE,
    startTime: DateFormatter({
      date: startTime,
      format: DateFormats['yyyy-MM-dd HH:mm:s'],
    }),
    endTime: DateFormatter({
      date: endTime,
      format: DateFormats['yyyy-MM-dd HH:mm:s'],
    }),
  };

  const queryKey = getClinicalNotesKey({
    ...filter,
    patientGuid,
    module,
  });

  const {
    mutate: deleteClinicalNotes,
    mutateAsync: deleteClinicalNotesAsync,
    isLoading,
    ...other
  } = useMutation({
    mutationFn: deleteClinicalNotesFn,
    onSuccess(_, variables) {
      notificationController.success({
        message: t('labels.commentDeleted'),
      });
      queryClient.setQueryData<IClinicNotesResponse>(queryKey, (oldData) => {
        if (oldData) {
          const newData = structuredClone(oldData);
          newData.count = newData.count > 0 ? (newData.count -= 1) : 0;
          newData.items = newData.items.filter(
            (item) => item.guid !== variables.guid,
          );

          if (newData.items.length >= CLINICAL_NITES_PER_PAGE)
            newData.items.length = CLINICAL_NITES_PER_PAGE;

          return newData;
        }
        return oldData;
      });
      const today = new Date();
      const formattedDate = DateFormatter({ date: today });
      const rpmInteractiveTime = getRpmInteractionTimeKey(
        patientGuid,
        formattedDate,
      );
      queryClient.invalidateQueries(rpmInteractiveTime);
      const ccmInteractiveTime = getCcmInteractionTimeKey(
        patientGuid,
        formattedDate,
      );
      queryClient.invalidateQueries(ccmInteractiveTime);
    },
    onError() {
      notificationController.error({
        message: t('labels.commentDeleteError'),
      });
    },
  });

  return {
    deleteClinicalNotes,
    deleteClinicalNotesAsync,
    deleteLoading: isLoading,
    ...other,
  };
};
